import { ReactElement } from 'react';
import { forgeClassHelper } from '../utils/classes';
import List, { ListProps } from '../List';

const classes = forgeClassHelper('interactive-list');

/**
 * Finds the unique key of the list item associated with an event that has
 * bubbled to the root ul element.
 */
const findUniqueKeyForEvent = (
  event: React.MouseEvent<HTMLUListElement, MouseEvent> | React.KeyboardEvent<HTMLUListElement>
): string | null => {
  if (event.target) {
    let li = event.target as HTMLElement | null;
    while (li && li.parentNode !== event.currentTarget) {
      li = li.parentNode as HTMLElement;
    }

    return li ? li.getAttribute('data-key') : null;
  } else {
    return null;
  }
};

interface UseOnClickItemArgs {
  disabled: boolean;
  onClickItem: InteractiveListProps['onClickItem'];
  onClick: InteractiveListProps['onClick'];
  onKeyPress: InteractiveListProps['onKeyPress'];
}

function useOnClickItem({
  disabled,
  onClickItem,
  onClick,
  onKeyPress,
}: UseOnClickItemArgs): Pick<InteractiveListProps, 'onClick' | 'onKeyPress'> {
  // As a convenience, pass the unique key of the corresponding list item
  // to the callback as the second parameter.
  if (!disabled && onClickItem) {
    return {
      onClick: (event: React.MouseEvent<HTMLUListElement, MouseEvent>) => {
        onClickItem(event, findUniqueKeyForEvent(event));
      },
      onKeyPress: (event: React.KeyboardEvent<HTMLUListElement>) => {
        if (event.charCode === 13 || event.charCode === 32) {
          // Enter or Spacebar
          onClickItem(event, findUniqueKeyForEvent(event));
        }
      },
    };
  }
  return { onClick, onKeyPress };
}

/** The props for an InteractiveList component */
export interface InteractiveListProps extends ListProps {
  /** Adds a class to the root element of the component */
  className?: string;

  /** Set to true to disable */
  disabled?: boolean;

  /**
   * A callback handling click events that have bubbled to the root component.
   * Arguments passed to the callback are the synthetic event and the unique key
   * of the associated list item.
   */
  onClickItem?: (
    event: React.MouseEvent<HTMLUListElement, MouseEvent> | React.KeyboardEvent<HTMLUListElement>,
    key: string | null
  ) => void;

  /** Set to true to style as padded */
  padded?: boolean;

  /** Set to true to display dividers */
  dividers?: boolean;
}

/**
 * InteractiveList
 *
 * A component to render an unordered list of items.
 *
 */
function InteractiveList({
  className,
  disabled = false,
  onClickItem,
  onClick: upstreamOnClick,
  onKeyPress: upstreamOnKeyPress,
  ...passthroughProps
}: InteractiveListProps): ReactElement {
  const { onClick, onKeyPress } = useOnClickItem({
    disabled,
    onClickItem,
    onClick: upstreamOnClick,
    onKeyPress: upstreamOnKeyPress,
  });

  const ariaDisabled = disabled ? true : undefined;

  const classNames = classes({
    extra: className,
    states: [disabled ? 'disabled' : ''],
  });

  return (
    <List
      onClick={onClick}
      onKeyPress={onKeyPress}
      aria-disabled={ariaDisabled}
      {...passthroughProps}
      {...classNames}
    />
  );
}

export default InteractiveList;
